import { useState, type ReactElement } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Checkbox } from 'app/components/ui/checkbox'
import { Field, FieldLabel } from 'app/components/ui/field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { CALL_TYPES, CHANNELS, TREATMENTS } from 'app/components/ops-evals/pipeline/schemas'
import { CALL_OUTCOMES, PATIENT_TYPES, emptyPerson, type GoldenExtraction, type GoldenPerson } from './mockData'

/** Radix Select items cannot hold an empty value, so "not set" has its own. */
const NONE = `__none`

function OptionalSelect({
  label,
  value,
  options,
  noneLabel,
  onChange,
}: {
  label: string
  value?: string
  options: string[]
  noneLabel: string
  onChange: (value: string | undefined) => void
}): ReactElement {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Select value={value || NONE} onValueChange={(next) => onChange(next === NONE ? undefined : next)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{noneLabel}</SelectItem>
          {options.filter(Boolean).map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  )
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string
  value?: string
  onChange: (value: string | undefined) => void
}): ReactElement {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <Input value={value ?? ``} onChange={(e) => onChange(e.target.value || undefined)} />
    </Field>
  )
}

function SectionTitle({ index, title }: { index: number; title: string }): ReactElement {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-muted text-[10px] font-semibold text-subtle">
        {index}
      </span>
      <span className="text-xs font-semibold text-default">{title}</span>
    </div>
  )
}

/** Editable form for a golden extraction. Manages its own draft; commits via onSave. */
export function GoldenEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: GoldenExtraction
  onSave: (golden: GoldenExtraction) => void
  onCancel: () => void
}): ReactElement {
  const [draft, setDraft] = useState<GoldenExtraction>(() => structuredClone(initial))

  const setGlobal = (key: 'answerer' | 'channel' | 'callType', value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value || undefined }))

  const setPerson = (index: number, patch: Partial<GoldenPerson>) =>
    setDraft((prev) => ({
      ...prev,
      people: prev.people.map((person, i) => (i === index ? { ...person, ...patch } : person)),
    }))

  const addPerson = () => setDraft((prev) => ({ ...prev, people: [...prev.people, emptyPerson()] }))
  const removePerson = (index: number) =>
    setDraft((prev) => ({ ...prev, people: prev.people.filter((_, i) => i !== index) }))

  const toggleOutcome = (index: number, outcome: string) =>
    setDraft((prev) => ({
      ...prev,
      people: prev.people.map((person, i) => {
        if (i !== index) return person
        const has = person.outcomes.includes(outcome)
        return {
          ...person,
          outcomes: has ? person.outcomes.filter((o) => o !== outcome) : [...person.outcomes, outcome],
        }
      }),
    }))

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase text-subtle">Edit expected extraction</h4>
        <div className="flex gap-2">
          <Button variant="white" size="xs" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="xs" onClick={() => onSave(draft)}>
            Save golden
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {/* Overview */}
        <div className="rounded-lg border border-default bg-surface p-3">
          <SectionTitle index={1} title="Overview" />
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField
              label="Answerer"
              value={draft.answerer}
              onChange={(value) => setGlobal(`answerer`, value ?? ``)}
            />
            <OptionalSelect
              label="Channel"
              value={draft.channel}
              options={CHANNELS}
              noneLabel="Not set"
              onChange={(value) => setGlobal(`channel`, value ?? ``)}
            />
            <OptionalSelect
              label="Call type"
              value={draft.callType}
              options={CALL_TYPES}
              noneLabel="Not graded"
              onChange={(value) => setGlobal(`callType`, value ?? ``)}
            />
          </div>
          <p className="mt-2 text-caption-sm text-subtle">
            Production does not save the call type, so leave it Not graded unless you want runs scored on it.
          </p>
        </div>

        {/* People + per-person appointment / outcome */}
        <div className="rounded-lg border border-default bg-surface p-3">
          <SectionTitle index={2} title="People, appointment & outcome" />
          <div className="space-y-3">
            {draft.people.map((person, i) => (
              <div key={i} className="rounded-lg border border-default bg-surface-subtle/60 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-default">Person {i + 1}</span>
                  <Button
                    variant="tertiary"
                    size="xs"
                    onClick={() => removePerson(i)}
                    disabled={draft.people.length === 1}
                  >
                    <TrashIcon className="mr-1 h-3.5 w-3.5" /> Remove
                  </Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field>
                    <FieldLabel>Name</FieldLabel>
                    <Input value={person.name} onChange={(e) => setPerson(i, { name: e.target.value })} />
                  </Field>
                  <Field>
                    <FieldLabel>Patient type</FieldLabel>
                    <Select value={person.patientType} onValueChange={(value) => setPerson(i, { patientType: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PATIENT_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <TextField
                    label="Relationship to caller"
                    value={person.relationship}
                    onChange={(relationship) => setPerson(i, { relationship })}
                  />
                  <TextField
                    label="Phone (10 digits)"
                    value={person.phone}
                    onChange={(phone) => setPerson(i, { phone })}
                  />
                  <TextField label="Email" value={person.email} onChange={(email) => setPerson(i, { email })} />
                  <TextField
                    label="Birth date (YYYY-MM-DD)"
                    value={person.birthDate}
                    onChange={(birthDate) => setPerson(i, { birthDate })}
                  />
                </div>

                <div className="mt-3 flex flex-wrap gap-4 text-xs">
                  <label className="flex items-center gap-1.5">
                    <Checkbox
                      checked={person.isCaller}
                      onCheckedChange={(v) => setPerson(i, { isCaller: v === true })}
                    />
                    Caller
                  </label>
                  <label className="flex items-center gap-1.5">
                    <Checkbox
                      checked={person.appointmentRequested}
                      onCheckedChange={(v) => setPerson(i, { appointmentRequested: v === true })}
                    />
                    Appt requested
                  </label>
                  <label className="flex items-center gap-1.5">
                    <Checkbox
                      checked={person.appointmentScheduled}
                      onCheckedChange={(v) => setPerson(i, { appointmentScheduled: v === true })}
                    />
                    Appt scheduled
                  </label>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <OptionalSelect
                    label="Treatment"
                    value={person.treatment}
                    options={TREATMENTS}
                    noneLabel="None mentioned"
                    onChange={(treatment) => setPerson(i, { treatment })}
                  />
                  <TextField
                    label="Service requested (graded on presence)"
                    value={person.serviceRequested}
                    onChange={(serviceRequested) => setPerson(i, { serviceRequested })}
                  />
                  {person.appointmentRequested && !person.appointmentScheduled && (
                    <TextField
                      label="Reason not scheduled (graded on presence)"
                      value={person.reasonForNotScheduling}
                      onChange={(reasonForNotScheduling) => setPerson(i, { reasonForNotScheduling })}
                    />
                  )}
                </div>

                {person.appointmentScheduled && (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Field>
                      <FieldLabel>Appt date (YYYY-MM-DD)</FieldLabel>
                      <Input
                        value={person.appointmentDate ?? ``}
                        onChange={(e) => setPerson(i, { appointmentDate: e.target.value || undefined })}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Appt time (h:mm a)</FieldLabel>
                      <Input
                        value={person.appointmentTime ?? ``}
                        onChange={(e) => setPerson(i, { appointmentTime: e.target.value || undefined })}
                      />
                    </Field>
                  </div>
                )}

                <div className="mt-3">
                  <div className="mb-1 text-[11px] uppercase text-minimal">Outcomes</div>
                  <div className="flex flex-wrap gap-1">
                    {CALL_OUTCOMES.map((outcome) => {
                      const active = person.outcomes.includes(outcome)
                      return (
                        <button
                          key={outcome}
                          onClick={() => toggleOutcome(i, outcome)}
                          className={
                            active
                              ? `rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-700`
                              : `rounded-full border border-default bg-surface px-2 py-0.5 text-xs text-subtle hover:bg-surface-subtle`
                          }
                        >
                          {outcome}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <TextField
                    label="Insurance provider"
                    value={person.insuranceProvider}
                    onChange={(insuranceProvider) => setPerson(i, { insuranceProvider })}
                  />
                  <TextField
                    label="Referral (falls back to the channel)"
                    value={person.referral}
                    onChange={(referral) => setPerson(i, { referral })}
                  />
                </div>
              </div>
            ))}
          </div>

          <Button variant="tertiary" size="sm" className="mt-3" onClick={addPerson}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" /> Add person
          </Button>
        </div>
      </div>
    </section>
  )
}
